import React, { useState, useCallback, useMemo, useEffect } from 'react';
import CgDialog from './CgDialog';
import {
  inputBaseClass,
  inputIdleClass,
  labelClass,
} from 'modules/Common/form/v2/primitives/inputClasses';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as faIcons from '@fortawesome/free-solid-svg-icons';
import { CgFilterSelect, CgFilterMultiSelect } from './CgFilterDropdown';
import CgButton from './CgButton';
import CgRadixButton from './CgRadixButton';
import CgPopover from './CgPopover';
import CgInfoHint from './CgInfoHint';
import CgDropdown from 'modules/Common/form/CgDropdown';
import {
  getTablePresets,
  saveTablePreset,
  deleteTablePreset,
  loadTablePreset,
  generateTableId,
  validatePresetFormData,
  getPresetSummary,
  findMatchingPreset,
  createCleanFormData,
  clearAllPresets,
  getAllPresetsData,
} from 'modules/Common/utils/searchPresetUtils';
import { formatDateTime, DATE_FORMAT } from 'helpers/dateParser';
import { useTranslation } from 'react-i18next';

/**
 * CgSearchFilter is a component that displays a set of search filters inside a popover.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {Array} props.filters - An array of filter objects defining the filters to be displayed.
 * @param {Object} props.formData - The current values of the form fields.
 * @param {Function} props.onFormChange - Callback function to handle changes in form fields.
 * @param {Function} props.onSubmit - Callback function to handle form submission.
 * @param {Function} props.onReset - Callback function to handle resetting the form fields.
 * @param {Function} props.onFiltersReady - Callback function to expose filters to parent components for column filtering.
 * @param {string} props.tableTitle - Title of the table for generating unique preset storage key.
 * @param {string} props.tableKey - Unique key for this table type (for preset isolation).
 * @param {string} props.tableContext - Optional context (e.g., page path) for unique preset storage.
 * @param {boolean} props.showActiveSummary - Hovering Filter lists the active filters (default).
 *   Off for a page that already shows them as chips (Suppliers).
 * @returns {JSX.Element} The rendered search filter component.
 */
const CgSearchFilter = ({
  filters = [],
  formData = {},
  onFormChange,
  onSubmit,
  onReset,
  onFiltersReady,
  tableTitle = 'Table',
  tableKey = null,
  tableContext = '',
  showActiveSummary = true,
}) => {
  const { t } = useTranslation('common');
  const [showModal, setShowModal] = useState(false);
  const [shouldSubmit, setShouldSubmit] = useState(false);
  const [pendingFormData, setPendingFormData] = useState(null);

  // Preset management state
  const [presets, setPresets] = useState([]);
  const [selectedPreset, setSelectedPreset] = useState('');
  const [showSavePresetModal, setShowSavePresetModal] = useState(false);
  const [presetName, setPresetName] = useState('');
  const [savePresetError, setSavePresetError] = useState('');

  const handleClose = useCallback(() => setShowModal(false), []);
  const handleShow = useCallback(() => setShowModal(true), []);

  // Generate table ID for preset storage
  const tableId = useMemo(() => {
    const id = generateTableId(tableTitle, tableKey, tableContext);
    return id;
  }, [tableTitle, tableKey, tableContext]);

  // Calculate active filters (defined early for other dependencies)
  const activeFilters = useMemo(() => {
    // Use the most current form data (including pending changes for immediate UI updates)
    const currentFormData = pendingFormData || formData;

    return filters.reduce((acc, filter) => {
      const value = currentFormData[filter.name];
      if (
        value === null ||
        value === undefined ||
        value === '' ||
        (Array.isArray(value) && value.length === 0)
      )
        return acc;

      let displayValue = value;
      if (filter.type === 'select') {
        const option = filter.options?.find((opt) => opt.value === value);
        displayValue = option ? option.label : value;
      } else if (filter.type === 'multi-select' && Array.isArray(value)) {
        const selectedOptions = (filter.options || [])
          .filter((opt) => value.includes(opt.value))
          .map((opt) => opt.label);
        displayValue = selectedOptions;
      }

      acc.push({
        key: filter.name,
        label: filter.label,
        value: displayValue,
      });
      return acc;
    }, []);
  }, [filters, formData, pendingFormData]);

  // Check if current form data matches any existing preset (defined early)
  const matchingPreset = useMemo(() => {
    const currentFormData = pendingFormData || formData;
    return findMatchingPreset(currentFormData, presets, filters);
  }, [formData, pendingFormData, presets, filters]);

  // Determine if save button should be shown (defined early)
  const shouldShowSaveButton = useMemo(() => {
    return activeFilters.length > 0 && !matchingPreset;
  }, [activeFilters.length, matchingPreset]);

  // Preset management functions (defined early to avoid reference errors)
  const handleSavePreset = useCallback(() => {
    setSavePresetError('');

    if (!presetName.trim()) {
      setSavePresetError(t('filters.presetNameRequired', 'Preset name is required'));
      return;
    }

    const success = saveTablePreset(tableId, presetName.trim(), formData, filters);

    if (success) {
      // Reload presets
      const updatedPresets = getTablePresets(tableId);
      setPresets(updatedPresets);
      setShowSavePresetModal(false);
      setPresetName('');
      setSavePresetError('');
    } else {
      setSavePresetError(t('filters.presetSaveFailed', 'Failed to save preset'));
    }
  }, [tableId, presetName, formData, filters]);

  const handleLoadPreset = useCallback(
    (presetId) => {
      if (!presetId) return;

      const preset = loadTablePreset(tableId, presetId);
      if (preset) {
        // First, create clean form data to clear all existing filters
        const cleanFormData = createCleanFormData(filters);

        // Then validate and apply preset data
        const validatedData = validatePresetFormData(preset.formData, filters);

        // Create new form data by starting with clean data and applying preset
        const newFormData = {
          ...cleanFormData,
          ...validatedData,
          page: 1, // Reset to first page when loading preset
        };

        onFormChange(newFormData);

        // Trigger search after loading preset
        if (onSubmit) {
          setTimeout(() => {
            onSubmit();
          }, 0);
        }
      }
    },
    [tableId, filters, onFormChange, onSubmit],
  );

  const handleDeletePreset = useCallback(
    (presetId) => {
      const success = deleteTablePreset(tableId, presetId);
      if (success) {
        // Reload presets
        const updatedPresets = getTablePresets(tableId);
        setPresets(updatedPresets);

        // Clear selection if deleted preset was selected
        if (selectedPreset === presetId) {
          setSelectedPreset('');
        }
      }
    },
    [tableId, selectedPreset],
  );

  // Debounce text input changes with 300ms delay
  useEffect(() => {
    if (!pendingFormData) return;

    const timer = setTimeout(() => {
      // Remove typing flag from pending form data before triggering search
      const cleanFormData = { ...pendingFormData };
      delete cleanFormData._isTyping;

      // Trigger the search logic with pending form data
      if (onFormChange) {
        onFormChange(cleanFormData);
      }
      if (onSubmit) {
        onSubmit();
      }

      // Clear pending data
      setPendingFormData(null);
    }, 300);

    return () => clearTimeout(timer);
  }, [pendingFormData, onFormChange, onSubmit]);

  // Load presets on component mount and when tableId changes
  useEffect(() => {
    const loadPresets = () => {
      // Make debugging functions globally available
      if (typeof window !== 'undefined') {
        window.debugPresets = {
          clearAll: clearAllPresets,
          getAllData: getAllPresetsData,
          getTablePresets: (id) => getTablePresets(id || tableId),
          currentTableId: tableId,
        };
      }

      const tablePresets = getTablePresets(tableId);
      setPresets(tablePresets);
    };
    loadPresets();
  }, [tableId]);

  // Expose filters and presets to parent component for column filtering.
  // The direct callback (onFiltersReady) is what CgTable uses; the global
  // event is what siblings of the table (e.g. ImportsSmartSegments above
  // the table) listen to. Both fire so both surfaces stay in sync from
  // a single source of truth.
  useEffect(() => {
    if (filters.length > 0) {
      const currentFormData = pendingFormData || formData;
      const presetPayload = {
        presets,
        handleLoadPreset,
        handleDeletePreset,
        shouldShowSaveButton,
        matchingPreset,
        onSavePreset: () => setShowSavePresetModal(true),
        tableId,
      };

      if (onFiltersReady) {
        onFiltersReady(filters, currentFormData, onFormChange, presetPayload);
      }

      // Always also broadcast on the global event so consumers that aren't
      // direct parents (segments rows, status bars) can subscribe too.
      const timer = setTimeout(() => {
        const filterReadyEvent = new CustomEvent('cgTableFilterReady', {
          detail: {
            filters,
            formData: currentFormData,
            onFormChange,
            presetData: presetPayload,
            tableId: window._cgTableCurrentId,
          },
        });
        window.dispatchEvent(filterReadyEvent);
      }, 10);

      return () => clearTimeout(timer);
    }
    return undefined;
  }, [
    filters,
    formData,
    pendingFormData,
    onFormChange,
    onFiltersReady,
    presets,
    handleLoadPreset,
    handleDeletePreset,
    shouldShowSaveButton,
    matchingPreset,
  ]);

  /**
   * Submits the filter form.
   */
  const handleSubmit = useCallback(() => {
    if (onSubmit) {
      onSubmit();
    }
    handleClose();
  }, [onSubmit, handleClose]);

  // Effect to handle form submissions after state updates
  useEffect(() => {
    if (shouldSubmit) {
      handleSubmit();
      setShouldSubmit(false);
    }
  }, [shouldSubmit, handleSubmit]);

  /**
   * Handles change events for text and number inputs.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} e - The change event.
   */
  const handleChange = useCallback(
    (e) => {
      const newFormData = {
        ...formData,
        [e.target.name]: e.target.value,
        auto: false,
        _isTyping: true, // Mark as typing to prevent immediate search
      };

      // For text and number inputs, don't call onFormChange immediately
      // Store the pending change and let the debounced effect handle it
      setPendingFormData(newFormData);
    },
    [formData],
  );

  /**
   * Handles changes for select and multi-select fields.
   *
   * @param {string} name - The name of the form field.
   * @param {any} value - The new value of the form field.
   */
  const handleFilterChange = useCallback(
    (name, value) => {
      const newFormData = {
        ...formData,
        [name]: value,
      };

      // For select fields, call onFormChange immediately
      onFormChange(newFormData);

      // For select fields, trigger immediate search
      if (onSubmit) {
        // Use setTimeout to ensure the form state is updated first
        setTimeout(() => {
          onSubmit();
        }, 0);
      }
    },
    [formData, onFormChange, onSubmit],
  );

  /**
   * Handles removing a single filter.
   *
   * @param {string} filterName - The name of the filter to remove.
   */
  const handleRemoveFilter = useCallback(
    (filterName) => {
      const resetValue =
        filters.find((f) => f.name === filterName)?.type === 'multi-select' ? [] : '';
      onFormChange({
        ...formData,
        [filterName]: resetValue,
        page: 1,
      });
      setShouldSubmit(true);
    },
    [filters, formData, onFormChange],
  );

  /**
   * Resets all filter fields to their default state.
   */
  const handleReset = useCallback(() => {
    const resetData = {
      name: '',
      contactEmail: '',
      country: null,
      goods: [],
      page: 1,
      limit: 10,
    };
    onReset(resetData);
    setShouldSubmit(true);
  }, [onReset]);

  const renderActiveFilters = useCallback(() => {
    if (activeFilters.length === 0) {
      return (
        <div className="p-3 text-sm text-gray-500 min-w-[200px] text-center bg-white">
          <FontAwesomeIcon
            icon={faIcons.faFilter}
            className="mr-2 opacity-70"
          />
          {t('filters.noActiveFilters', 'No active filters')}
        </div>
      );
    }

    return (
      <div className="flex items-center gap-2 flex-wrap p-2 min-w-[300px] bg-white">
        {activeFilters.map(({ key, label, value }) => (
          <CgPopover
            key={key}
            triggerElement={
              <div className="bg-gray-100 text-gray-700 px-3 py-1.5 rounded-full flex items-center gap-2 text-sm max-w-[200px] group cursor-default">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="font-medium whitespace-nowrap text-primaryColor">{label}:</span>
                  <span className="text-gray-600 truncate">
                    {Array.isArray(value) ? value.join(', ') : value}
                  </span>
                </div>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleRemoveFilter(key);
                  }}
                  className="hover:text-red-500 transition-colors ml-1 shrink-0"
                  title={t('common:tableControls.removeFilterTitle', {
                    filter: label,
                    defaultValue: 'Remove {{filter}} filter',
                  })}
                >
                  <FontAwesomeIcon
                    icon={faIcons.faTimes}
                    size="sm"
                  />
                </button>
              </div>
            }
            popoverContent={
              <div className="p-2 text-sm max-w-[300px]">
                <span className="font-medium text-primaryColor">{label}:</span>{' '}
                {Array.isArray(value) ? value.join(', ') : value}
              </div>
            }
            position="top"
            trigger="hover"
            openDelay={0}
            closeDelay={0}
          />
        ))}
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleReset();
          }}
          className="text-xs text-primaryColor hover:text-primaryColor/80 font-medium flex items-center gap-1 transition-colors"
          title={t('filters.clearAllFilters', 'Clear all filters')}
        >
          <FontAwesomeIcon
            icon={faIcons.faTimesCircle}
            size="xs"
          />
          {t('actions.clearAll', 'Clear all')}
        </button>
      </div>
    );
  }, [activeFilters, handleRemoveFilter, handleReset]);

  /**
   * Renders a filter label, optionally with an info-icon CgPopover beside it
   * when `filter.labelTooltip` is provided.
   */
  const renderFilterLabel = useCallback((filter) => {
    if (!filter.labelTooltip) return filter.label;
    return (
      <span className="inline-flex items-center gap-1.5">
        <span>{filter.label}</span>
        <CgInfoHint
          subject={filter.label}
          position="top"
          maxWidth="max-w-[260px]"
          stopPropagation
        >
          {filter.labelTooltip}
        </CgInfoHint>
      </span>
    );
  }, []);

  // Prepare preset options for dropdown
  const presetOptions = useMemo(() => {
    return presets.map((preset) => ({
      value: preset.id,
      label: preset.name,
      preset: preset,
    }));
  }, [presets]);

  // Render preset selector popover content
  const renderPresetSelector = useCallback(() => {
    if (presets.length === 0) {
      return (
        <div className="p-3 text-sm text-gray-500 min-w-[250px] text-center bg-white">
          <FontAwesomeIcon
            icon={faIcons.faBookmark}
            className="mr-2 opacity-70"
          />
          {t('filters.noSavedPresets', 'No saved presets')}
          <div className="mt-2 text-xs text-gray-400">
            {t('filters.presetsEmptyHint', 'Configure filters and save a preset to get started')}
          </div>
        </div>
      );
    }

    return (
      <div className="p-2 min-w-[300px] bg-white">
        <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-100">
          <FontAwesomeIcon
            icon={faIcons.faBookmark}
            className="text-primaryColor"
          />
          <span className="font-medium text-gray-900 text-sm">
            {t('filters.savedPresets', 'Saved Presets')}
          </span>
        </div>
        <div className="space-y-1 max-h-[200px] overflow-y-auto">
          {presets.map((preset) => (
            <div
              key={preset.id}
              className="flex items-center justify-between p-2 rounded hover:bg-gray-50 group"
            >
              <div className="flex-1 min-w-0">
                <button
                  onClick={() => {
                    handleLoadPreset(preset.id);
                    setSelectedPreset(preset.id);
                  }}
                  className="text-left w-full"
                >
                  <div className="font-medium text-sm text-gray-900 truncate">{preset.name}</div>
                  <div className="text-xs text-gray-500 truncate">
                    {getPresetSummary(preset, filters)}
                  </div>
                </button>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeletePreset(preset.id);
                }}
                className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700 p-1 transition-all"
                title={t('filters.deletePreset', 'Delete preset')}
              >
                <FontAwesomeIcon
                  icon={faIcons.faTrash}
                  size="xs"
                />
              </button>
            </div>
          ))}
        </div>
      </div>
    );
  }, [presets, filters, handleLoadPreset, handleDeletePreset]);

  const filterButton = (
    <CgRadixButton
      variant="secondary"
      size="sm"
      icon={faIcons.faFilter}
      onClick={handleShow}
    >
      {t('common:filters.filter', 'Filter')}
      {activeFilters.length > 0 && (
        <span className="ml-1 whitespace-nowrap rounded-full bg-primaryColor/10 px-1.5 py-0.5 text-[10px] font-semibold text-primaryColor tabular-nums">
          {activeFilters.length}
        </span>
      )}
    </CgRadixButton>
  );

  return (
    <div className="relative inline-block">
      {showActiveSummary ? (
        <CgPopover
          triggerElement={filterButton}
          popoverContent={renderActiveFilters()}
          popoverContentClassName="min-w-max"
          position="top"
          trigger="hover"
          openDelay={0}
          closeDelay={150}
          mainAxisOffset={10}
          sideOffset={0}
        />
      ) : (
        filterButton
      )}

      <CgDialog
        open={showModal}
        onOpenChange={(o) => !o && handleClose()}
        title={t('filters.title', 'Filters')}
        closeOnOverlayClick={false}
        footer={
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <CgRadixButton
                variant="ghost"
                icon={faIcons.faSync}
                onClick={handleReset}
              >
                {t('actions.clear', 'Clear')}
              </CgRadixButton>
            </div>
            <CgRadixButton
              variant="primary"
              icon={faIcons.faSearch}
              onClick={handleSubmit}
            >
              {t('actions.search', 'Search')}
            </CgRadixButton>
          </div>
        }
      >
        <>
          {/* Preset Management Section */}
          {presets.length > 0 && (
            <div className="mb-4 p-3 bg-gray-50 rounded-lg border">
              <div className="flex items-center justify-between mb-3">
                <h6 className="text-sm font-semibold text-gray-800 mb-0">
                  <FontAwesomeIcon
                    icon={faIcons.faBookmark}
                    className="mr-2 text-primaryColor"
                  />
                  {t('filters.savedPresets', 'Saved Presets')}
                </h6>
                {matchingPreset && (
                  <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                    {t('common:filters.currentlyApplied', {
                      name: matchingPreset.name,
                      defaultValue: 'Currently applied: {{name}}',
                    })}
                  </span>
                )}
              </div>
              <div className="grid grid-cols-1 gap-2">
                {presets.map((preset) => (
                  <div
                    key={preset.id}
                    className="flex items-center justify-between p-2 bg-white rounded border hover:bg-gray-50 group"
                  >
                    <div className="flex-1 min-w-0">
                      <CgPopover
                        triggerElement={
                          <button
                            onClick={() => {
                              handleLoadPreset(preset.id);
                              setSelectedPreset(preset.id);
                              handleClose();
                            }}
                            className="text-left w-full"
                          >
                            <div className="font-medium text-sm text-gray-900 truncate">
                              {preset.name}
                            </div>
                            <div className="text-xs text-gray-500 truncate">
                              {getPresetSummary(preset, filters)}
                            </div>
                          </button>
                        }
                        popoverContent={
                          <div className="p-3 max-w-[350px]">
                            <div className="font-medium text-sm text-gray-900 mb-2">
                              {preset.name}
                            </div>
                            <div className="text-xs text-gray-600 mb-3">
                              {t('common:tableControls.savedOn', {
                                date: formatDateTime(preset.createdAt, DATE_FORMAT.SHORT),
                                defaultValue: 'Saved: {{date}}',
                              })}
                              {preset.updatedAt !== preset.createdAt && (
                                <span className="ml-1">
                                  {t('common:tableControls.updatedOn', {
                                    date: formatDateTime(preset.updatedAt, DATE_FORMAT.SHORT),
                                    defaultValue: '(Updated: {{date}})',
                                  })}
                                </span>
                              )}
                            </div>
                            {preset.formData && Object.keys(preset.formData).length > 0 ? (
                              <div className="space-y-2">
                                <div className="text-xs font-medium text-gray-700 uppercase tracking-wide">
                                  {t('filters.presetFilters', 'Preset Filters:')}
                                </div>
                                <div className="flex flex-wrap gap-1">
                                  {Object.entries(preset.formData).map(([key, value]) => {
                                    const filter = filters.find((f) => f.name === key);
                                    if (
                                      !filter ||
                                      !value ||
                                      (Array.isArray(value) && value.length === 0)
                                    )
                                      return null;

                                    let displayValue = value;
                                    if (filter.type === 'select') {
                                      const option = filter.options?.find(
                                        (opt) => opt.value === value,
                                      );
                                      displayValue = option ? option.label : value;
                                    } else if (
                                      filter.type === 'multi-select' &&
                                      Array.isArray(value)
                                    ) {
                                      if (value.length === 1) {
                                        const option = filter.options?.find(
                                          (opt) => opt.value === value[0],
                                        );
                                        displayValue = option ? option.label : value[0];
                                      } else {
                                        displayValue = t('common:filter.selectedCount', {
                                          count: value.length,
                                        });
                                      }
                                    }

                                    return (
                                      <div
                                        key={key}
                                        className="bg-gray-100 text-gray-700 px-2 py-1 rounded-full text-xs"
                                      >
                                        <span className="font-medium text-primaryColor">
                                          {filter.label}:
                                        </span>{' '}
                                        {displayValue}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            ) : (
                              <div className="text-xs text-gray-500 italic">
                                {t('filters.noFiltersSaved', 'No filters saved')}
                              </div>
                            )}
                          </div>
                        }
                        position="right"
                        trigger="hover"
                        openDelay={500}
                        closeDelay={200}
                      />
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeletePreset(preset.id);
                      }}
                      className="text-red-500 hover:text-red-700 p-1 transition-all ml-2 opacity-70 hover:opacity-100"
                      title={t('filters.deletePreset', 'Delete preset')}
                    >
                      <FontAwesomeIcon
                        icon={faIcons.faTrash}
                        size="xs"
                      />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex flex-col w-full">
            {filters?.map((filter, index) => {
              if (filter?.type === 'text' || filter?.type === 'number' || filter?.type === 'date') {
                return (
                  <div
                    key={index}
                    className={`w-full ${filter?.className || 'mb-4'}`}
                  >
                    <label
                      htmlFor={filter?.name}
                      className={labelClass}
                    >
                      {renderFilterLabel(filter)}
                    </label>
                    <input
                      id={filter?.name}
                      type={filter?.type}
                      placeholder={filter?.placeholder || filter?.label}
                      name={filter?.name}
                      value={
                        pendingFormData && pendingFormData[filter?.name] !== undefined
                          ? pendingFormData[filter?.name]
                          : formData[filter?.name] || ''
                      }
                      onChange={handleChange}
                      min={filter?.min}
                      max={filter?.max}
                      step={filter?.step}
                      className={`${inputBaseClass} ${inputIdleClass}`}
                    />
                  </div>
                );
              }

              if (filter.type === 'select') {
                return (
                  <CgFilterSelect
                    key={index}
                    className={`w-full ${filter.className || 'mb-4'}`}
                    label={!filter.disableLabel ? renderFilterLabel(filter) : null}
                    options={filter.options ?? []}
                    value={formData[filter.name] ?? null}
                    onChange={(val) => handleFilterChange(filter.name, val ?? '')}
                    placeholder={
                      filter.placeholder ||
                      t('common:filters.selectFilterPlaceholder', {
                        label: filter.label,
                        defaultValue: 'Select {{label}}',
                      })
                    }
                    isClearable
                    filterOption={filter.filterOption}
                    searchable={filter.searchable}
                  />
                );
              }

              if (filter.type === 'multi-select') {
                return (
                  <CgFilterMultiSelect
                    key={index}
                    className={`w-full ${filter.className || 'mb-4'}`}
                    label={!filter.disableLabel ? renderFilterLabel(filter) : null}
                    options={filter.options ?? []}
                    value={formData[filter.name] ?? []}
                    onChange={(vals) => handleFilterChange(filter.name, vals)}
                    placeholder={
                      filter.placeholder ||
                      t('common:filters.selectFilterPlaceholder', {
                        label: filter.label,
                        defaultValue: 'Select {{label}}',
                      })
                    }
                    isClearable
                    filterOption={filter.filterOption}
                    searchable={filter.searchable}
                  />
                );
              }

              return null;
            })}
          </div>
        </>
      </CgDialog>

      {/* Save Preset Modal */}
      <CgDialog
        open={showSavePresetModal}
        onOpenChange={(o) => {
          if (!o) {
            setShowSavePresetModal(false);
            setPresetName('');
            setSavePresetError('');
          }
        }}
        title={t('filters.savePresetTitle', 'Save Filter Preset')}
        closeOnOverlayClick={false}
        footer={
          <div className="flex justify-between items-center">
            <CgRadixButton
              variant="secondary"
              onClick={() => {
                setShowSavePresetModal(false);
                setPresetName('');
                setSavePresetError('');
              }}
            >
              {t('actions.cancel', 'Cancel')}
            </CgRadixButton>
            <CgRadixButton
              variant="primary"
              icon={faIcons.faSave}
              onClick={handleSavePreset}
              disabled={!presetName.trim()}
            >
              {t('filters.savePreset', 'Save Preset')}
            </CgRadixButton>
          </div>
        }
      >
        <div className="flex flex-col w-full">
          <div className="w-full mb-3">
            <label className={labelClass}>{t('filters.presetName', 'Preset Name')}</label>
            <input
              type="text"
              placeholder={t('filters.presetNamePlaceholder', 'Enter preset name')}
              value={presetName}
              onChange={(e) => {
                setPresetName(e.target.value);
                setSavePresetError('');
              }}
              maxLength={50}
              autoFocus
              className={`${inputBaseClass} ${inputIdleClass}`}
            />
          </div>
          {savePresetError && <div className="text-red-500 text-sm mb-2">{savePresetError}</div>}
          <div className="text-xs text-gray-500 mb-3">
            {t(
              'common:filters.saveHint',
              'Current filters will be saved with this name. If a preset with the same name exists, it will be updated.',
            )}
          </div>
          {activeFilters.length > 0 && (
            <div className="bg-gray-50 p-3 rounded border">
              <div className="text-xs font-medium text-gray-700 mb-2">
                {t('filters.preview', 'Preview:')}
              </div>
              <div className="text-xs text-gray-600">
                {activeFilters
                  .map(
                    (filter) =>
                      `${filter.label}: ${Array.isArray(filter.value) ? filter.value.join(', ') : filter.value}`,
                  )
                  .join(' • ')}
              </div>
            </div>
          )}
        </div>
      </CgDialog>
    </div>
  );
};

export default CgSearchFilter;
